import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { calibrationService, DEFAULT_GROUP_KEY } from "../../../../../../services/CalibrationService";
import type { RootState } from "../store";
import { AnnotatedDataset, MasterdataRecords, MasterdataRecordsUpload } from "../../../types";
import { Draft } from "@reduxjs/toolkit";
import { addTimeoutToPromise } from "../../../util";

export type DataRetrievalStatus = "idle" | "loading" | "succeeded" | "failed";
export type OperationStatus = "idle" | "loading" | "succeeded" | "failed" | "timeout";

export type MdUploadToolAllowedDatasetType = "masterdata-records" | "masterdata-records-upload";
export interface MdUploadToolState {
    // basics - client and project, and related datasets
    client: string;
    project: string;
    datasetType?: MdUploadToolAllowedDatasetType;
    groupKey: string;
    versionDttm: string;

    dataset?: AnnotatedDataset<MasterdataRecords> | AnnotatedDataset<MasterdataRecordsUpload>;
    datasetLoadingStatus: DataRetrievalStatus;
    datasetLoadingError?: string;

    // bulk upload
    bulkUploadStatus: OperationStatus;
    bulkUploadError?: string;

    // bulk teardown
    bulkTeardownStatus: OperationStatus;
    bulkTeardownError?: string;

    // regenerate records
    regenerateRecordsStatus: OperationStatus;
    regenerateRecordsError?: string;

    selectedTable?: string;

    // conductor properties validation
    conductorPropertiesStatus: DataRetrievalStatus;
    conductorPropertiesError?: string;
    conductorPropertiesExist: boolean;
}

const initialState = {
    client: "",
    project: "",
    datasetType: undefined,
    groupKey: "",
    versionDttm: "",
    dataset: undefined,
    datasetLoadingStatus: "idle",
    datasetLoadingError: undefined,
    bulkUploadStatus: "idle",
    bulkUploadError: undefined,
    bulkTeardownStatus: "idle",
    bulkTeardownError: undefined,
    regenerateRecordsStatus: "idle",
    regenerateRecordsError: undefined,
    selectedTable: undefined,
    conductorPropertiesStatus: "idle",
    conductorPropertiesError: undefined,
    conductorPropertiesExist: false,
} as MdUploadToolState;

const resetDatasetRelatedState = (state: Draft<MdUploadToolState>) => {
    state.dataset = undefined;
    state.datasetLoadingStatus = "idle";
    state.datasetLoadingError = undefined;
    state.bulkUploadStatus = "idle";
    state.bulkUploadError = undefined;
    state.bulkTeardownStatus = "idle";
    state.bulkTeardownError = undefined;
    state.regenerateRecordsStatus = "idle";
    state.regenerateRecordsError = undefined;
    state.selectedTable = undefined;
    state.conductorPropertiesStatus = "idle";
    state.conductorPropertiesError = undefined;
    state.conductorPropertiesExist = false;
};

const extractErrorFromRejectedAction = (action: any): string => {
    // When using rejectWithValue, error is in action.payload
    // When throwing directly, error is in action.error.message
    if (action.payload) {
        return typeof action.payload === "string" ? action.payload : action.payload?.toString() || "Unknown error";
    }
    return action.error?.message || "Unknown error";
};
export const loadDataset = createAsyncThunk("mdUpload/loadDataset", async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const client = state.mdUpload.client;
    const project = state.mdUpload.project;
    const datasetType = state.mdUpload.datasetType;
    const groupKey = state.mdUpload.groupKey;
    const versionDttm = state.mdUpload.versionDttm;

    if (!client || !project || !datasetType || !groupKey || !versionDttm) {
        throw new Error("Missing required fields");
    }

    try {
        const dataset = await calibrationService.getExactDataset(client, project, groupKey, datasetType, versionDttm);
        if (datasetType === "masterdata-records-upload") {
            return dataset as AnnotatedDataset<MasterdataRecordsUpload>;
        }
        return dataset as AnnotatedDataset<MasterdataRecords>;
    } catch (error: any) {
        const errorMessage = typeof error === "string" ? error : error?.message || "An error occurred loading dataset";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const startBulkUpload = createAsyncThunk("mdUpload/bulkUpload", async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const client = state.mdUpload.client;
    const project = state.mdUpload.project;
    const datasetType = state.mdUpload.datasetType;
    const groupKey = state.mdUpload.groupKey;
    const versionDttm = state.mdUpload.versionDttm;

    if (!client || !project || !datasetType || !groupKey || !versionDttm) {
        throw new Error("Missing required fields");
    }

    if (datasetType !== "masterdata-records") {
        throw new Error("Invalid dataset type");
    }

    try {
        const response = await addTimeoutToPromise(
            calibrationService.uploadMasterdata(client, project, datasetType, groupKey, versionDttm)
        );
        if (response === "timeout") {
            return "timeout";
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        return response?.data;
    } catch (error: any) {
        const errorMessage = typeof error === "string" ? error : error?.message || "An error occurred during upload";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const startBulkTeardown = createAsyncThunk("mdUpload/bulkTeardown", async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const client = state.mdUpload.client;
    const project = state.mdUpload.project;
    const datasetType = state.mdUpload.datasetType;
    const groupKey = state.mdUpload.groupKey;
    const versionDttm = state.mdUpload.versionDttm;

    if (!client || !project || !datasetType || !groupKey || !versionDttm) {
        throw new Error("Missing required fields");
    }

    if (datasetType !== "masterdata-records-upload") {
        throw new Error("Invalid dataset type");
    }

    try {
        const response = await addTimeoutToPromise(
            calibrationService.teardownMasterdata(client, project, datasetType, groupKey, versionDttm)
        );
        if (response === "timeout") {
            return "timeout";
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        return response?.data;
    } catch (error: any) {
        const errorMessage = typeof error === "string" ? error : error?.message || "An error occurred during teardown";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const startRegenerateRecords = createAsyncThunk("mdUpload/regenerateRecords", async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;

    const selectedClient = state.mdUpload.client;
    const selectedProject = state.mdUpload.project;

    if (!selectedClient || !selectedProject) {
        return thunkAPI.rejectWithValue("Missing required fields");
    }

    try {
        const response = await addTimeoutToPromise(
            calibrationService.regenerateMasterdataRecords(selectedClient, selectedProject)
        );
        if (response === "timeout") {
            return "timeout";
        }
        if (response?.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response?.data;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error?.toString() || "An error occurred while regenerating master data records");
    }
});

export const checkConductorProperties = createAsyncThunk("mdUpload/checkConductorProperties", async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const client = state.mdUpload.client;
    const project = state.mdUpload.project;
    const groupKey = state.mdUpload.groupKey;
    const versionDttm = state.mdUpload.versionDttm;

    if (!client || !project || !versionDttm) {
        throw new Error("Missing required fields");
    }

    if (groupKey !== DEFAULT_GROUP_KEY) {
        return { all_exist: true, message: "" };
    }

    try {
        const response = await calibrationService.checkConductorPropertiesExist(client, project, versionDttm);
        return response;
    } catch (error: any) {
        const errorMessage =
            error?.response?.data?.error || error?.response?.data?.message || error?.message || "An error occurred checking conductor properties";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const mdUploadSlice = createSlice({
    name: "mdUpload",
    initialState,
    reducers: {
        setClient: (state, action: PayloadAction<string>) => {
            state.client = action.payload;
            resetDatasetRelatedState(state);
        },
        setProject: (state, action: PayloadAction<string>) => {
            state.project = action.payload;
            resetDatasetRelatedState(state);
        },
        setDatasetType: (state, action: PayloadAction<MdUploadToolAllowedDatasetType>) => {
            state.datasetType = action.payload;
            resetDatasetRelatedState(state);
        },
        setGroupKey: (state, action: PayloadAction<string>) => {
            state.groupKey = action.payload;
            resetDatasetRelatedState(state);
        },
        setVersionDttm: (state, action: PayloadAction<string>) => {
            state.versionDttm = action.payload;
            resetDatasetRelatedState(state);
        },
        setSelectedTable(state, action: PayloadAction<string>) {
            state.selectedTable = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadDataset.pending, (state) => {
            state.datasetLoadingStatus = "loading";
            state.dataset = undefined;
            state.datasetLoadingError = undefined;
        });
        builder.addCase(loadDataset.fulfilled, (state, action) => {
            state.datasetLoadingStatus = "succeeded";
            state.dataset = action.payload;
            state.selectedTable = Object.keys(action.payload.data)[0];
        });
        builder.addCase(loadDataset.rejected, (state, action) => {
            state.datasetLoadingStatus = "failed";
            state.datasetLoadingError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(startBulkUpload.pending, (state) => {
            state.bulkUploadStatus = "loading";
            state.bulkUploadError = undefined;
        });
        builder.addCase(startBulkUpload.fulfilled, (state, action) => {
            if (action.payload === "timeout") {
                state.bulkUploadStatus = "timeout";
                return;
            }
            state.bulkUploadStatus = "succeeded";
        });
        builder.addCase(startBulkUpload.rejected, (state, action) => {
            state.bulkUploadStatus = "failed";
            state.bulkUploadError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(startBulkTeardown.pending, (state) => {
            state.bulkTeardownStatus = "loading";
            state.bulkTeardownError = undefined;
        });
        builder.addCase(startBulkTeardown.fulfilled, (state, action) => {
            if (action.payload === "timeout") {
                state.bulkTeardownStatus = "timeout";
                return;
            }
            state.bulkTeardownStatus = "succeeded";
        });
        builder.addCase(startBulkTeardown.rejected, (state, action) => {
            state.bulkTeardownStatus = "failed";
            state.bulkTeardownError = extractErrorFromRejectedAction(action);
        });
        builder.addCase(startRegenerateRecords.pending, (state) => {
            state.regenerateRecordsStatus = "loading";
            state.regenerateRecordsError = undefined;
        });
        builder.addCase(startRegenerateRecords.fulfilled, (state, action) => {
            if (action.payload === "timeout") {
                state.regenerateRecordsStatus = "timeout";
                return;
            }
            state.regenerateRecordsStatus = "succeeded";
        });
        builder.addCase(startRegenerateRecords.rejected, (state, action) => {
            state.regenerateRecordsStatus = "failed";
            state.regenerateRecordsError = (action.payload as string) || extractErrorFromRejectedAction(action);
        });
        builder.addCase(checkConductorProperties.pending, (state) => {
            state.conductorPropertiesStatus = "loading";
            state.conductorPropertiesError = undefined;
            state.conductorPropertiesExist = false;
        });
        builder.addCase(checkConductorProperties.fulfilled, (state, action) => {
            state.conductorPropertiesStatus = "succeeded";
            if (action.payload.all_exist) {
                state.conductorPropertiesExist = true;
            } else {
                state.conductorPropertiesExist = false;
                state.conductorPropertiesError = action.payload.message;
            }
        });
        builder.addCase(checkConductorProperties.rejected, (state, action) => {
            state.conductorPropertiesStatus = "failed";
            state.conductorPropertiesError = extractErrorFromRejectedAction(action);
            state.conductorPropertiesExist = false;
        });
    },
});

export const { setClient, setProject, setDatasetType, setGroupKey, setVersionDttm, setSelectedTable } =
    mdUploadSlice.actions;

export const selectCurrentClient = (state: RootState) => state.mdUpload.client;
export const selectCurrentProject = (state: RootState) => state.mdUpload.project;
export const selectCurrentDatasetType = (state: RootState): string => (state.mdUpload.datasetType || "") as string;
export const selectCurrentGroupKey = (state: RootState) => state.mdUpload.groupKey;
export const selectCurrentVersionDttm = (state: RootState) => state.mdUpload.versionDttm;
export const selectCurrentDataset = (state: RootState) => state.mdUpload.dataset;
export const selectCurrentDatasetMetadata = (state: RootState) => state.mdUpload.dataset?.meta;
export const selectDatasetLoadingStatus = (state: RootState) => state.mdUpload.datasetLoadingStatus;
export const selectDatasetLoadingError = (state: RootState) => state.mdUpload.datasetLoadingError;

export const selectBulkUploadStatus = (state: RootState) => state.mdUpload.bulkUploadStatus;
export const selectBulkUploadError = (state: RootState) => state.mdUpload.bulkUploadError;
export const selectBulkTeardownStatus = (state: RootState) => state.mdUpload.bulkTeardownStatus;
export const selectBulkTeardownError = (state: RootState) => state.mdUpload.bulkTeardownError;
export const selectRegenerateRecordsStatus = (state: RootState) => state.mdUpload.regenerateRecordsStatus;
export const selectRegenerateRecordsError = (state: RootState) => state.mdUpload.regenerateRecordsError;

export const selectConductorPropertiesStatus = (state: RootState) => state.mdUpload.conductorPropertiesStatus;
export const selectConductorPropertiesError = (state: RootState) => state.mdUpload.conductorPropertiesError;
export const selectConductorPropertiesExist = (state: RootState) => state.mdUpload.conductorPropertiesExist;
export const selectConductorPropertiesIsLoading = (state: RootState) => state.mdUpload.conductorPropertiesStatus === "loading";

export const selectDatasetIsLoading = (state: RootState) => state.mdUpload.datasetLoadingStatus === "loading";
export const selectBulkUploadIsLoading = (state: RootState) => state.mdUpload.bulkUploadStatus === "loading";
export const selectBulkTeardownIsLoading = (state: RootState) => state.mdUpload.bulkTeardownStatus === "loading";
export const selectRegenerateRecordsIsLoading = (state: RootState) =>
    state.mdUpload.regenerateRecordsStatus === "loading";

export const selectBulkUploadIsReady = createSelector(
    [
        selectCurrentClient,
        selectCurrentProject,
        selectCurrentDatasetType,
        selectCurrentGroupKey,
        selectCurrentVersionDttm,
        selectCurrentDataset,
        selectConductorPropertiesExist,
    ],
    (client, project, datasetType, groupKey, versionDttm, currentDataset, conductorPropertiesExist) =>
        client &&
        project &&
        datasetType &&
        datasetType === "masterdata-records" &&
        groupKey &&
        versionDttm &&
        currentDataset &&
        conductorPropertiesExist
);
export const selectBulkTeardownIsReady = createSelector(
    [
        selectCurrentClient,
        selectCurrentProject,
        selectCurrentDatasetType,
        selectCurrentGroupKey,
        selectCurrentVersionDttm,
        selectCurrentDataset,
    ],
    (client, project, datasetType, groupKey, versionDttm, currentDataset) =>
        client &&
        project &&
        datasetType &&
        datasetType === "masterdata-records-upload" &&
        groupKey &&
        versionDttm &&
        currentDataset &&
        currentDataset.data?.teardown !== "complete"
);
export const selectRegenerateRecordsIsReady = createSelector(
    [selectCurrentClient, selectCurrentProject, selectCurrentGroupKey],
    (client, project, groupKey) => client && project && (!groupKey || groupKey === DEFAULT_GROUP_KEY)
);

export const selectTableDataIsAvailable = createSelector(
    [selectCurrentDatasetType, selectCurrentDataset],
    (datasetType, currentDataset) =>
        !!(datasetType === "masterdata-records" && !!currentDataset?.data) ||
        !!(
            datasetType === "masterdata-records-upload" &&
            currentDataset?.data &&
            currentDataset?.data?.teardown !== "complete"
        )
);
export const selectSelectedTable = createSelector(
    [selectTableDataIsAvailable, (state: RootState) => state.mdUpload.selectedTable],
    (tableDataIsAvailable, selectedTable) => (tableDataIsAvailable ? selectedTable : undefined)
);

export const selectAvailableTables = createSelector(
    [selectCurrentDataset, selectTableDataIsAvailable],
    (currentDataset, tableDataIsAvailable) => {
        if (!currentDataset || !tableDataIsAvailable) {
            return [];
        }
        return Object.keys(currentDataset.data);
    }
);
export const selectSelectedTableData = createSelector(
    [selectCurrentDataset, selectSelectedTable, selectTableDataIsAvailable],
    (currentDataset, selectedTable, selectTableDataIsAvailable) =>
        selectedTable && selectTableDataIsAvailable ? ((currentDataset?.data as any)[selectedTable] as any[]) : []
);
export const selectSelectedTableDataNumRows = createSelector([selectSelectedTableData], (tableData) =>
    tableData ? tableData.length : 0
);

export const selectSelectedTableColumnNames = createSelector([selectSelectedTableData], (tableData) =>
    tableData && tableData.length > 0 ? Object.keys(tableData[0]) : []
);

export default mdUploadSlice;
